import React, { useState, useEffect, useCallback } from "react";
import { ChevronLeft, ChevronRight, X, Images } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";

/**
 * 5 catégories cliquables. Chaque carte → lightbox avec galerie défilante.
 *
 * Pour ajouter de nouvelles photos :
 *   1. Uploadez vos images dans /app/frontend/public/portfolio/{categorie}/
 *      (ex: /app/frontend/public/portfolio/cuisines/projet-1.jpg)
 *   2. Ajoutez le chemin dans le tableau `photos` de la catégorie ci-dessous.
 */
const CATEGORIES = [
  {
    id: "cuisines",
    title: "Cuisines sur mesure",
    cover: "/portfolio/cuisines/cuisine-2-vue-mer.jpg",
    tag: "Résidentiel · La Réunion",
    description: "De la cuisine ouverte conviviale aux îlots imposants. Chaque cuisine est conçue selon votre espace, vos habitudes et votre budget.",
    photos: [
      { src: "/portfolio/cuisines/cuisine-2-vue-mer.jpg", alt: "Cuisine contemporaine noire avec vue mer · La Réunion" },
      { src: "/portfolio/cuisines/cuisine-1-mezzanine.jpg", alt: "Cuisine industrielle sous mezzanine, plan de travail bois et façades grises" },
      { src: "/portfolio/cuisines/cuisine-3-ilot-bois.jpg", alt: "Cuisine moderne bois et bleu avec îlot central" },
      { src: "/portfolio/cuisines/cuisine-4-bois-clair.jpg", alt: "Cuisine ouverte bois clair avec îlot et grand plan de travail" },
      { src: "/portfolio/cuisines/cuisine-5-blanc-laque.jpg", alt: "Cuisine blanc laqué avec plan de travail noir brillant" },
      { src: "/portfolio/cuisines/cuisine-6-compacte.jpg", alt: "Cuisine compacte en L, façades grises et plan de travail bois" },
    ],
  },
  {
    id: "dressings",
    title: "Dressings & rangements",
    cover: "/portfolio/dressings/dressing-0-parental-bois.jpg",
    tag: "Résidentiel · Sur mesure",
    description: "Dressings cintrés, portes coulissantes, sous-pente, éclairage LED intégré : on optimise chaque centimètre carré, du grand dressing parental au placard d'appoint.",
    photos: [
      { src: "/portfolio/dressings/dressing-0-parental-bois.jpg", alt: "Grand dressing parental en bois clair avec rangements ouverts et accès chambre" },
      { src: "/portfolio/dressings/dressing-1-bois-led.jpg", alt: "Dressing bois péï avec éclairage LED intégré" },
      { src: "/portfolio/dressings/dressing-2-portes-bleu.jpg", alt: "Dressing avec portes coulissantes bleu canard, tiroirs et niches" },
      { src: "/portfolio/dressings/dressing-3-blanc-moderne.jpg", alt: "Dressing ouvert blanc moderne, étagères et penderies" },
      { src: "/portfolio/dressings/dressing-4-bois-sous-pente.jpg", alt: "Dressing bois sous pente avec tiroirs et niches" },
      { src: "/portfolio/dressings/dressing-5-bois-cintres.jpg", alt: "Dressing bois ouvert avec chambre tropicale et ventilateur" },
    ],
  },
  {
    id: "salles-de-bain",
    title: "Salles de bain",
    cover: "/portfolio/salles-de-bain/sdb-1-vasque-miroir-led.jpg",
    tag: "Résidentiel · Bien-être",
    description: "Salles de bain modernes, douches à l'italienne, meubles vasque sur mesure adaptés à vos contraintes techniques.",
    photos: [
      { src: "/portfolio/salles-de-bain/sdb-1-vasque-miroir-led.jpg", alt: "Salle de bain moderne avec meuble vasque bois, vasque blanche et miroir LED" },
      { src: "/portfolio/salles-de-bain/sdb-2-douche-italienne.jpg", alt: "Douche à l'italienne avec mosaïque graphique et receveur extra-plat" },
    ],
  },
  {
    id: "meubles",
    title: "Mobilier sur mesure",
    cover: "/portfolio/meubles/meuble-1-entree-niches.jpg",
    tag: "Mobilier · Fabrication locale",
    description: "Meubles TV, bibliothèques, rangements d'entrée, buffets, claustras, tables et mobilier divers : chaque pièce conçue et fabriquée localement, à votre image.",
    photos: [
      { src: "/portfolio/meubles/meuble-1-entree-niches.jpg", alt: "Rangement d'entrée sur mesure blanc et bois avec niches éclairées" },
      { src: "/mobilier-meubletv.jpg", alt: "Meuble TV bois et métal sur mesure" },
      { src: "/mobilier-buffet.png", alt: "Buffet sur mesure" },
      { src: "/portfolio-2-meuble.jpg", alt: "Bibliothèque sur mesure avec éclairage LED intégré" },
      { src: "/mobilier-claustra.jpg", alt: "Claustra bois ajouré séparateur de pièce" },
      { src: "/mobilier-bureau.jpg", alt: "Bureau sur mesure intégré" },
      { src: "/mobilier-table.jpg", alt: "Table salle à manger bois massif" },
    ],
  },
  {
    id: "commerces",
    title: "Commerces & pro",
    cover: "/portfolio/commerces/commerce-1-librairie-reunion.jpg",
    tag: "Professionnels · Aménagement",
    description: "Aménagement de librairies, boutiques de souvenirs, maroquineries, bureaux de tabac et supérettes. Mobilier, présentoirs et rayonnages adaptés à chaque univers commercial.",
    photos: [
      { src: "/portfolio/commerces/commerce-1-librairie-reunion.jpg", alt: "Librairie réunionnaise avec étagères murales noires et fond turquoise" },
      { src: "/portfolio/commerces/commerce-2-boutique-souvenirs.jpg", alt: "Boutique de souvenirs et produits du terroir avec étagères murales et fond turquoise" },
      { src: "/portfolio/commerces/commerce-3-maroquinerie.jpg", alt: "Maroquinerie avec présentoirs muraux pour sacs et valises au centre" },
      { src: "/portfolio/commerces/commerce-4-tabac-presse.jpg", alt: "Bureau de tabac-presse avec comptoir bois et présentoirs cigarettes" },
      { src: "/portfolio/commerces/commerce-5-superette.jpg", alt: "Supérette de proximité avec rayonnages bois et présentoirs métal" },
    ],
  },
];

export default function Portfolio() {
  const [openCat, setOpenCat] = useState(null);
  const [photoIdx, setPhotoIdx] = useState(0);

  const openCategory = (cat) => {
    setOpenCat(cat);
    setPhotoIdx(0);
  };

  const close = () => setOpenCat(null);

  const next = useCallback(() => {
    if (!openCat) return;
    setPhotoIdx((i) => (i + 1) % openCat.photos.length);
  }, [openCat]);

  const prev = useCallback(() => {
    if (!openCat) return;
    setPhotoIdx((i) => (i - 1 + openCat.photos.length) % openCat.photos.length);
  }, [openCat]);

  // Keyboard navigation
  useEffect(() => {
    if (!openCat) return;
    const onKey = (e) => {
      if (e.key === "ArrowRight") next();
      else if (e.key === "ArrowLeft") prev();
      else if (e.key === "Escape") close();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [openCat, next, prev]);

  return (
    <section
      id="realisations"
      data-testid="portfolio-section"
      className="bg-white py-24 sm:py-32"
    >
      <div className="mx-auto max-w-7xl px-6">
        <div className="mb-14 flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <div className="max-w-xl">
            <p className="mb-4 text-xs uppercase tracking-[0.25em] text-orange-800">
              Réalisations
            </p>
            <h2 className="text-3xl font-light leading-tight tracking-tight text-stone-900 sm:text-5xl">
              Quelques projets,
              <br />
              <span className="italic">beaucoup de matière.</span>
            </h2>
          </div>
          <p className="max-w-md text-base text-stone-600">
            Cliquez sur une rubrique pour découvrir l'ensemble des photos.
            Le prochain projet sera le vôtre.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {CATEGORIES.map((cat) => (
            <button
              key={cat.id}
              type="button"
              onClick={() => openCategory(cat)}
              data-testid={`portfolio-card-${cat.id}`}
              className="group relative h-[360px] overflow-hidden rounded-2xl border border-stone-200 text-left transition-shadow hover:shadow-[0_24px_60px_-20px_rgba(28,25,23,0.4)] focus:outline-none focus:ring-2 focus:ring-orange-800 focus:ring-offset-2"
            >
              <img
                src={cat.cover}
                alt={cat.title}
                className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-stone-950/85 via-stone-950/30 to-transparent" />

              <div className="absolute top-4 right-4">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-stone-50/95 px-3 py-1.5 text-xs font-medium text-stone-900 backdrop-blur">
                  <Images className="h-3.5 w-3.5" strokeWidth={1.5} />
                  {cat.photos.length} {cat.photos.length > 1 ? "photos" : "photo"}
                </span>
              </div>

              <div className="absolute bottom-0 left-0 right-0 p-6">
                <p className="text-xs uppercase tracking-[0.22em] text-stone-300">{cat.tag}</p>
                <h3 className="mt-1 text-xl font-medium text-stone-50">{cat.title}</h3>
                <p className="mt-2 text-sm text-stone-200/90 line-clamp-2">
                  {cat.description}
                </p>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Lightbox */}
      <Dialog open={!!openCat} onOpenChange={(v) => !v && close()}>
        <DialogContent
          data-testid="portfolio-lightbox"
          className="max-w-5xl border-stone-200 bg-stone-50 p-0 sm:rounded-3xl"
        >
          {openCat && (
            <>
              <DialogTitle className="sr-only">{openCat.title}</DialogTitle>
              <DialogDescription className="sr-only">{openCat.description}</DialogDescription>

              <div className="relative">
                <img
                  src={openCat.photos[photoIdx].src}
                  alt={openCat.photos[photoIdx].alt}
                  className="aspect-[4/3] w-full rounded-t-3xl object-cover"
                />

                {openCat.photos.length > 1 && (
                  <>
                    <button
                      type="button"
                      onClick={prev}
                      aria-label="Photo précédente"
                      data-testid="lightbox-prev"
                      className="absolute left-3 top-1/2 -translate-y-1/2 rounded-full bg-stone-900/70 p-2.5 text-stone-50 backdrop-blur transition-colors hover:bg-stone-900"
                    >
                      <ChevronLeft className="h-5 w-5" strokeWidth={1.5} />
                    </button>
                    <button
                      type="button"
                      onClick={next}
                      aria-label="Photo suivante"
                      data-testid="lightbox-next"
                      className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full bg-stone-900/70 p-2.5 text-stone-50 backdrop-blur transition-colors hover:bg-stone-900"
                    >
                      <ChevronRight className="h-5 w-5" strokeWidth={1.5} />
                    </button>
                  </>
                )}

                <div className="absolute top-4 left-4 rounded-full bg-stone-900/70 px-3 py-1.5 text-xs font-medium text-stone-50 backdrop-blur">
                  {photoIdx + 1} / {openCat.photos.length}
                </div>
              </div>

              <div className="p-6 sm:p-8">
                <p className="text-xs uppercase tracking-[0.22em] text-orange-800">{openCat.tag}</p>
                <h3 className="mt-2 text-2xl font-light tracking-tight text-stone-900 sm:text-3xl">
                  {openCat.title}
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-stone-600">
                  {openCat.description}
                </p>

                {/* Thumbnails */}
                {openCat.photos.length > 1 && (
                  <div className="mt-6 flex gap-2 overflow-x-auto pb-1">
                    {openCat.photos.map((p, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => setPhotoIdx(i)}
                        aria-label={`Voir photo ${i + 1}`}
                        data-testid={`lightbox-thumb-${i}`}
                        className={`relative h-16 w-24 shrink-0 overflow-hidden rounded-lg border-2 transition-all ${
                          i === photoIdx
                            ? "border-orange-800"
                            : "border-stone-200 opacity-60 hover:opacity-100"
                        }`}
                      >
                        <img
                          src={p.src}
                          alt={p.alt}
                          className="h-full w-full object-cover"
                        />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
